// Derived view model of the Energy tab: the merged per-slot timeline (actual past + plan), runs of equal
// action, the "right now" power flow, and the Energy chart colours (shared tokens: components/ui/tokens.ts).
// No React here.
import { EnergyPlanSnapshot, EnergyPlanSlot, EnergyPowerSeries, SolarState, VentLevel } from '../../common';
import { CO2_GOOD_LINE, CO2_HIGH_LINE, VENT_COLOR } from '../components/ui/tokens';
import { dayKey, hhmm, MINUS, MONTHS } from './format';
import { tallinnParts } from './tallinn_time';

export const SLOT_MS = 15 * 60_000;
export const HOUR_MS = 3_600_000;
export const CONTROLLER_FLOOR_PERCENT = 13; // older snapshots don't send socFloorPercent
export const PLAN_STALE_AFTER_MS = 30 * 60_000;
export const LIVE_STALE_AFTER_MS = 60_000;

// 'export': the plan stores less of the PV surplus than Self Use would (often nothing) and sends the
// rest to the grid; the battery still covers the house when the load exceeds PV. 'outage' (past slots only): the
// grid was out, the inverter ran the house off its backup output and the optimizer was paused.
export type Action = EnergyPlanSlot['action'] | 'outage';

// color: timeline strip, tint: chips and the plan pill, bar: the "Coming up" bar (Self Use needs more contrast).
export const ACTIONS: Record<Action, { label: string; color: string; tint: string; bar: string }> = {
  charge: { label: 'Charge', color: '#1baf7a', tint: '#d4f1e6', bar: '#1baf7a' },
  export: { label: 'Export', color: '#eda100', tint: '#fbecc6', bar: '#eda100' },
  hold: { label: 'Hold', color: '#8b7fe0', tint: '#e6e2fb', bar: '#8b7fe0' },
  discharge: { label: 'Discharge', color: '#eb6834', tint: '#fbe1d6', bar: '#eb6834' },
  auto: { label: 'Self Use', color: '#ecebe6', tint: '#ecebe6', bar: '#d6d5ce' },
  outage: { label: 'Grid outage', color: '#4a4a46', tint: '#dcdad3', bar: '#4a4a46' },
};

export const COLORS = {
  importPrice: '#2a78d6',
  importPriceEstimated: '#8fb6e8',
  exportPrice: '#1baf7a',
  spot: '#3d3d3a',
  socPlan: '#6250d6',
  socActual: '#3d3d3a',
  gridImport: '#e34948',
  gridExport: '#1baf7a',
  load: '#eb6834',
  pv: '#eda100',
  vent: VENT_COLOR,
  noExport: '#d03b3b',
  now: '#d03b3b',
  floor: '#d03b3b',
  zero: '#c3c2b7',
  co2Good: CO2_GOOD_LINE,
  co2High: CO2_HIGH_LINE,
};

// One row per 15-minute slot on the merged timeline: past slots from `actual`, the rest from the plan.
export interface Row {
  start: number;
  past: boolean;
  action?: Action;
  batteryPowerW?: number;
  curtailExport?: boolean;
  spot?: number; // Nord Pool, c/kWh excl VAT
  importPrice?: number;
  exportPrice?: number;
  estimatedPrice?: boolean;
  socStartPercent?: number;
  socEndPercent?: number;
  gridKwh?: number;
  loadKwh?: number;
  pvKwh?: number;
  outdoorTempC?: number;
  ventLevel?: VentLevel;
}

export interface ActionRun {
  start: number;
  end: number;
  action: Action;
  past: boolean;
  rows: Row[];
}

// Snapshots and history rows from before the controller had an 'export' action sent those slots as
// 'charge' (often 0 W) with net grid export.
function displayAction(action: EnergyPlanSlot['action'] | undefined, gridKwh: number | undefined): Action | undefined {
  return action === 'charge' && gridKwh !== undefined && gridKwh < -0.005 ? 'export' : action;
}

export function buildRows(plan: EnergyPlanSnapshot): Row[] {
  const planStart = plan.slots.length > 0 ? plan.slots[0].start : plan.createdAt;
  const rows: Row[] = (plan.actual ?? [])
    .filter((a) => a.start < planStart)
    .sort((a, b) => a.start - b.start)
    .map((a) => ({ ...a, action: a.gridOutage ? 'outage' : displayAction(a.action, a.gridKwh), past: true }));
  // Actual slots only carry the SoC at their end: take the start from the slot before.
  for (let i = 1; i < rows.length; i++) {
    if (rows[i - 1].start + SLOT_MS === rows[i].start) {
      rows[i].socStartPercent = rows[i - 1].socEndPercent;
    }
  }
  for (const s of plan.slots) {
    rows.push({ ...s, action: displayAction(s.action, s.gridKwh), past: false });
  }
  return rows;
}

export function buildRuns(rows: Row[]): ActionRun[] {
  const runs: ActionRun[] = [];
  for (const row of rows) {
    if (row.action === undefined) {
      continue;
    }
    const last = runs[runs.length - 1];
    if (last && last.action === row.action && last.past === row.past && last.end === row.start) {
      last.end = row.start + SLOT_MS;
      last.rows.push(row);
    } else {
      runs.push({ start: row.start, end: row.start + SLOT_MS, action: row.action, past: row.past, rows: [row] });
    }
  }
  return runs;
}

// The plan slot containing `now` (the first one when the plan is from before it).
export function currentPlanSlot(plan: EnergyPlanSnapshot, now: number): EnergyPlanSlot | undefined {
  return plan.slots.find((s) => s.start <= now && now < s.start + SLOT_MS) ?? plan.slots[0];
}

// ---- Formatting ------------------------------------------------------------------------------------

export function euros(cents: number): string {
  return `${cents < 0 ? MINUS : ''}€${(Math.abs(cents) / 100).toFixed(2)}`;
}

// "Tue 17:00–17:15"
export const slotLabel = (start: number) => `${tallinnParts(start).weekday} ${hhmm(start)}–${hhmm(start + SLOT_MS)}`;

export const monthName = (yyyyMm: string) => MONTHS[Number(yyyyMm.slice(5, 7)) - 1] ?? yyyyMm;

export function priceTier(c: number): { label: string; tint: string } {
  return c < 15
    ? { label: 'Cheap', tint: '#cde2fb' }
    : c < 30
      ? { label: 'Normal', tint: '#ecebe6' }
      : { label: 'Peak', tint: '#fbd5c4' };
}

// ---- Derived blocks --------------------------------------------------------------------------------

export interface NoExportBlocks {
  when: string; // "Wed 11:45–13:30, 15:00–15:30"
  lowestExportPrice: number;
}

// Contiguous runs of planned slots (from the current one on) with export blocked.
export function noExportBlocks(plan: EnergyPlanSnapshot, now: number): NoExportBlocks | undefined {
  const blocks: [number, number][] = [];
  let low = Infinity;
  for (const s of plan.slots) {
    if (s.start + SLOT_MS <= now || !s.curtailExport) {
      continue;
    }
    low = Math.min(low, s.exportPrice);
    const last = blocks[blocks.length - 1];
    if (last && last[1] === s.start) {
      last[1] = s.start + SLOT_MS;
    } else {
      blocks.push([s.start, s.start + SLOT_MS]);
    }
  }
  if (blocks.length === 0) {
    return undefined;
  }
  const today = dayKey(now);
  return {
    when: blocks
      .map(([a, b]) => `${dayKey(a) === today ? '' : `${tallinnParts(a).weekday} `}${hhmm(a)}–${hhmm(b)}`)
      .join(', '),
    lowestExportPrice: Number.isFinite(low) ? low : 0,
  };
}

// Power right now in kW: the 10-second live state, or the current plan slot's values without one.
export interface FlowNow {
  pvKw: number;
  loadKw: number;
  gridKw: number; // + import, - export
  batteryKw: number; // + charge, - discharge
  socPercent: number;
  source: 'live' | 'stale' | 'plan';
  receivedAt?: number;
}

export function flowNow(plan: EnergyPlanSnapshot, live: SolarState | undefined, now: number): FlowNow | undefined {
  if (live && live.consumePowerWatts !== undefined) {
    const stale = live.receivedAt !== undefined && now - live.receivedAt > LIVE_STALE_AFTER_MS;
    return {
      pvKw: live.solarProductionWatts / 1000,
      loadKw: live.consumePowerWatts / 1000,
      gridKw: -live.feedInPowerWatts / 1000,
      batteryKw: live.batteryPowerWatts / 1000,
      socPercent: live.batteryPercentage,
      source: stale ? 'stale' : 'live',
      receivedAt: live.receivedAt,
    };
  }
  const s = currentPlanSlot(plan, now);
  if (!s) {
    return undefined;
  }
  const kw = (kwh: number) => kwh * (HOUR_MS / SLOT_MS);
  // Battery from the slot's energy balance: grid + PV = load + battery.
  return {
    pvKw: kw(s.pvKwh),
    loadKw: kw(s.loadKwh),
    gridKw: kw(s.gridKwh),
    batteryKw: kw(s.gridKwh + s.pvKwh - s.loadKwh),
    socPercent: s.socStartPercent,
    source: 'plan',
  };
}

// The 5-minute interval of the power series containing x, if any.
export function powerIndex(power: EnergyPowerSeries | undefined, x: number): number | undefined {
  if (!power) {
    return undefined;
  }
  const i = Math.floor((x - power.start) / power.stepMs);
  return i >= 0 && i < power.loadW.length ? i : undefined;
}
